import {
  BadgeCheckIcon,
  BoardAddIcon,
  BoardRemoveIcon,
  BrainIcon,
  CalendarDaysIcon,
  CaptionsIcon,
  ContrastIcon,
  CropIcon,
  FileStackIcon,
  FormIcon,
  FrameIcon,
  ImagesIcon,
  LayoutGridIcon,
  LightbulbIcon,
  ListChecksIcon,
  MessageCircleQuestionIcon,
  MessagesSquareIcon,
  MoveBoardIcon,
  PackageCheckIcon,
  PaletteIcon,
  PencilIcon,
  RemoveBackgroundIcon,
  ReplaceIcon,
  ScalingIcon,
  ScanSearchIcon,
  ShirtIcon,
  SparklesIcon,
  SquareRoundCornerIcon,
  StampIcon,
  SunMediumIcon,
  TagIcon,
  VideoIcon,
} from '@air/design-icons';
import { SOCIAL_MEDIA_PLATFORM_GROUPS } from '@air/feature-saved-sizes/utils';

import { type GuidedVariantEditId } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentGuidedVariantEdits';
import { type WorkspaceAgentIconComponent } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/types';

export type WorkspaceAgentActionsMenuGroupId = 'ask_and_explore' | 'edit_in_bulk' | 'organize_in_bulk';

export type WorkspaceAgentActionsMenuGroup = {
  description: string;
  icon: WorkspaceAgentIconComponent;
  /** Tailwind text color for the group's glyph — each group has its own hue. */
  iconColorClassName: string;
  id: WorkspaceAgentActionsMenuGroupId;
  label: string;
};

export type WorkspaceAgentActionsMenuTask = {
  groupId: WorkspaceAgentActionsMenuGroupId;
  icon: WorkspaceAgentIconComponent;
  id: string;
  /** Search terms beyond the label. */
  keywords: readonly string[];
  label: string;
  prompt: string;
};

/** Social platforms only — the agent cannot see the workspace's saved size sets, so those are not search terms. */
const SOCIAL_PLATFORM_KEYWORDS = SOCIAL_MEDIA_PLATFORM_GROUPS.flatMap((group) => [group.id, group.label]);

export const WORKSPACE_AGENT_ACTIONS_MENU_GROUPS = [
  {
    description: 'Make, resize, restyle, and animate',
    icon: SparklesIcon,
    iconColorClassName: 'ads:text-ads-altitude-9 text-altitude-9',
    id: 'edit_in_bulk',
    label: 'Edit in bulk',
  },
  {
    description: 'Rename, tag, and fill in fields',
    icon: ListChecksIcon,
    iconColorClassName: 'ads:text-ads-grass-9 text-grass-9',
    id: 'organize_in_bulk',
    label: 'Organize in bulk',
  },
  {
    description: 'Get answers and ideas about your assets',
    icon: MessageCircleQuestionIcon,
    iconColorClassName: 'ads:text-ads-ember-9 text-ember-9',
    id: 'ask_and_explore',
    label: 'Ask and explore',
  },
] as const satisfies readonly WorkspaceAgentActionsMenuGroup[];

export const WORKSPACE_AGENT_ACTIONS_MENU_TASKS: readonly WorkspaceAgentActionsMenuTask[] = [
  {
    groupId: 'edit_in_bulk',
    icon: VideoIcon,
    id: 'animate',
    keywords: [],
    label: 'Animate',
    prompt:
      'Turn the selected images into short videos. Ask me about length and motion before you save new files. Leave the originals unchanged.',
  },
  {
    groupId: 'edit_in_bulk',
    icon: FileStackIcon,
    id: 'resize-for-socials',
    keywords: SOCIAL_PLATFORM_KEYWORDS,
    label: 'Resize for socials and channels',
    prompt:
      'Resize the selected assets for the social platforms I pick, like Instagram, TikTok, or LinkedIn. Ask me which before you save new assets on the current board.',
  },
  {
    groupId: 'edit_in_bulk',
    icon: SparklesIcon,
    id: 'make-more-like-this',
    keywords: [],
    label: 'Make more like this',
    prompt:
      'Make 10 more images like the selected asset. First, ask me what to riff on: mood, content, style, or something else. Save them as new assets on the current board. Leave the original unchanged.',
  },
  {
    groupId: 'edit_in_bulk',
    icon: CropIcon,
    id: 'crop',
    keywords: [],
    label: 'Crop',
    prompt: 'Crop the selected assets. Ask me how to frame them before you save new files.',
  },
  {
    groupId: 'edit_in_bulk',
    icon: ReplaceIcon,
    id: 'change-extension',
    keywords: [],
    label: 'Change extension',
    prompt: 'Change the file extension of the selected assets. Ask me which extension before you save new files.',
  },
  {
    groupId: 'edit_in_bulk',
    icon: StampIcon,
    id: 'watermark',
    keywords: [],
    label: 'Watermark',
    prompt: 'Add a watermark to the selected assets. Ask me which mark and where it sits before you save new files.',
  },
  {
    groupId: 'edit_in_bulk',
    icon: RemoveBackgroundIcon,
    id: 'remove-background',
    keywords: [],
    label: 'Remove background',
    prompt: 'Remove the background from the selected assets and save the results as new files.',
  },
  {
    groupId: 'edit_in_bulk',
    icon: ContrastIcon,
    id: 'grayscale',
    keywords: [],
    label: 'Grayscale',
    prompt: 'Make grayscale versions of the selected assets and save them as new files.',
  },
  {
    groupId: 'edit_in_bulk',
    icon: SquareRoundCornerIcon,
    id: 'rounded-corners',
    keywords: ['corners', 'round'],
    label: 'Rounded corners',
    prompt: 'Round the corners of the selected assets. Ask me how rounded before you save new files.',
  },
  {
    groupId: 'edit_in_bulk',
    icon: ScalingIcon,
    id: 'resize',
    keywords: [],
    label: 'Resize',
    prompt: 'Resize the selected assets. Ask me for the dimensions before you save new files.',
  },
  {
    groupId: 'edit_in_bulk',
    icon: PaletteIcon,
    id: 'stylize',
    keywords: [],
    label: 'Stylize',
    prompt:
      'Restyle the selected assets in a new look. Ask me which style or reference to follow before you save new files. Leave the originals unchanged.',
  },
  {
    groupId: 'edit_in_bulk',
    icon: FrameIcon,
    id: 'add-borders',
    keywords: [],
    label: 'Add borders',
    prompt: 'Add a border to the selected assets. Ask me about color and thickness before you save new files.',
  },
  {
    groupId: 'edit_in_bulk',
    icon: SunMediumIcon,
    id: 'adjust-lighting',
    keywords: [],
    label: 'Adjust lighting',
    prompt:
      'Adjust the lighting on the selected assets. Ask me what to change, such as brightness, warmth, or contrast, before you save new files.',
  },
  {
    groupId: 'edit_in_bulk',
    icon: ShirtIcon,
    id: 'product-shot-cleanup',
    keywords: [],
    label: 'Product shot cleanup',
    prompt:
      'Clean up the selected product shots: remove the background, center the product on white, and crop to a square. Save new files and leave the originals unchanged.',
  },
  {
    groupId: 'organize_in_bulk',
    icon: PencilIcon,
    id: 'rename',
    keywords: [],
    label: 'Rename',
    prompt:
      'Rename the selected assets using a naming convention I provide. Show me a before and after before you execute the task.',
  },
  {
    groupId: 'organize_in_bulk',
    icon: TagIcon,
    id: 'edit-tags',
    keywords: [],
    label: 'Edit tags',
    prompt:
      'Edit tags on the selected assets. Ask me which tags to add or remove, and show me the change before you apply it.',
  },
  {
    groupId: 'organize_in_bulk',
    icon: FormIcon,
    id: 'edit-custom-fields',
    keywords: [],
    label: 'Edit custom fields',
    prompt:
      'Edit custom fields on the selected assets. Ask me which fields and values to set, and show me the change before you apply it.',
  },
  {
    groupId: 'organize_in_bulk',
    icon: LightbulbIcon,
    id: 'suggest-tags',
    keywords: [],
    label: 'Suggest tags',
    prompt:
      'Suggest tags for the selected assets based on their content. Lead with a scannable list. Do not change tags until I approve.',
  },
  {
    groupId: 'organize_in_bulk',
    icon: BoardAddIcon,
    id: 'add-to-board',
    keywords: [],
    label: 'Add to board',
    prompt:
      'Add the selected assets to a board I pick. Ask me which board, and confirm the board before you add them. Keep them on the current board too.',
  },
  {
    groupId: 'organize_in_bulk',
    icon: MoveBoardIcon,
    id: 'move-to-board',
    keywords: [],
    label: 'Move to board',
    prompt:
      'Move the selected assets from the current board to a board I pick. Ask me which board, and confirm the move before you apply it.',
  },
  {
    groupId: 'organize_in_bulk',
    icon: BoardRemoveIcon,
    id: 'remove-from-board',
    keywords: [],
    label: 'Remove from board',
    prompt:
      'Remove the selected assets from the current board. List what will be removed and wait for me to confirm. Do not delete the assets.',
  },
  {
    groupId: 'organize_in_bulk',
    icon: PackageCheckIcon,
    id: 'prep-for-handoff',
    keywords: [],
    label: 'Prep for handoff',
    prompt:
      'Get the selected assets ready to hand off. Ask me for the naming convention, the tags they need, and which custom field and value to set. Show me every change before you apply it.',
  },
  {
    groupId: 'ask_and_explore',
    icon: LayoutGridIcon,
    id: 'board-overview',
    keywords: [],
    label: "What's on this board?",
    prompt:
      'Summarize what is on the current board: the main themes, formats, and anything that looks missing. Keep it short and scannable.',
  },
  {
    groupId: 'ask_and_explore',
    icon: ScanSearchIcon,
    id: 'whats-missing',
    keywords: [],
    label: "What's missing?",
    prompt:
      'Look at the selected assets and tell me what is missing, such as sizes, formats, or variations I would usually need. Do not make anything yet.',
  },
  {
    groupId: 'ask_and_explore',
    icon: ImagesIcon,
    id: 'find-similar',
    keywords: [],
    label: 'Find similar assets',
    prompt:
      'Find assets in this workspace that look similar to the selected assets, including near-duplicates. List them without changing anything.',
  },
  {
    groupId: 'ask_and_explore',
    icon: BrainIcon,
    id: 'brainstorm',
    keywords: [],
    label: 'Brainstorm ideas',
    prompt:
      'Brainstorm ideas for the next round based on the selected assets. Give me a short list of directions I could try.',
  },
  {
    groupId: 'ask_and_explore',
    icon: CaptionsIcon,
    id: 'write-captions',
    keywords: [],
    label: 'Write captions',
    prompt: 'Write caption options for the selected assets. Ask me about tone and platform first.',
  },
  {
    groupId: 'ask_and_explore',
    icon: MessagesSquareIcon,
    id: 'review-comments',
    keywords: [],
    label: 'Review comments',
    prompt: 'Review comments on the selected assets. Tell me what is unresolved and what still needs a response.',
  },
  {
    groupId: 'ask_and_explore',
    icon: BadgeCheckIcon,
    id: 'review-approvals',
    keywords: [],
    label: 'Review approvals',
    prompt: 'Review approval status on the selected assets.',
  },
  {
    groupId: 'ask_and_explore',
    icon: CalendarDaysIcon,
    id: 'weekly-recap',
    keywords: [],
    label: 'Weekly recap',
    prompt:
      'Recap what changed on this board this week: new assets, comments, and approvals. Keep it short and scannable.',
  },
];

/** Edit in bulk tasks that open the guided-variant wizard (WORK-969) instead of seeding the composer prompt. */
export const WORKSPACE_AGENT_ACTIONS_MENU_TASK_GUIDED_VARIANT_EDIT_ID: Record<string, GuidedVariantEditId> = {
  crop: 'resize',
  grayscale: 'grayscale',
  'remove-background': 'remove-background',
  'resize-for-socials': 'smart-resize',
  resize: 'resize',
  'rounded-corners': 'rounded-corners',
  watermark: 'watermark',
};

export const getWorkspaceAgentActionsMenuTaskGuidedVariantEditId = (taskId: string): GuidedVariantEditId | undefined =>
  WORKSPACE_AGENT_ACTIONS_MENU_TASK_GUIDED_VARIANT_EDIT_ID[taskId];
